import * as XLSX from "xlsx-js-style";
import { MRT_Row, MRT_TableInstance } from "material-react-table";
import { triggerDownload } from "./downloadTrigger";

export type RowData = Record<string, unknown>;

const EXCLUDED_COLUMN_IDS = [
  "mrt-row-select",
  "mrt-row-actions",
  "mrt-row-numbers",
];

const formatDate = (value: Date) =>
  value
    .toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })
    .replace(/ /g, "-");

const expandRowsWithChildren = <T extends RowData>(
  rows: MRT_Row<T>[]
): MRT_Row<T>[] => {
  const expandedRows: MRT_Row<T>[] = [];
  const addedIds = new Set<string>();

  rows.forEach((row) => {
    if (addedIds.has(row.id)) return;

    expandedRows.push(row);
    addedIds.add(row.id);

    const children = row.original?._children;
    if (!Array.isArray(children) || children.length === 0) return;

    const parentIndex = row.original?._displayIndex ?? "";

    children.forEach((childData: any, childIdx: number) => {
      const childDisplayIndex = parentIndex
        ? `${parentIndex}.${childIdx + 1}`
        : `${childIdx + 1}`;

      const childRow = {
        ...row,
        id: `${row.id}_child_${childIdx}`,
        original: {
          ...childData,
          _isChild: true,
          _parentExecId: row.original?.exec_id,
          _displayIndex: childDisplayIndex,
        },
        getValue: (columnId: string) =>
          columnId === "_displayIndex"
            ? childDisplayIndex
            : childData[columnId],
      } as MRT_Row<T>;

      if (!addedIds.has(childRow.id)) {
        expandedRows.push(childRow);
        addedIds.add(childRow.id);
      }
    });
  });

  return expandedRows;
};


const buildExportData = <T extends RowData>(
  table: MRT_TableInstance<T>,
  rows: MRT_Row<T>[],
  includeChildren: boolean = true
) => {
  const rowsToExport = includeChildren ? expandRowsWithChildren(rows) : rows;

  const columns = table
    .getVisibleLeafColumns()
    .filter((c) => !EXCLUDED_COLUMN_IDS.includes(c.id));

  const headers = columns.map(
    (c) => c.columnDef.header as string
  );

  const data = rowsToExport.map((row) => {
    return columns.map((col) => {
      const value = row.getValue(col.id);

      if (value == null) return "";
      if (value instanceof Date) return formatDate(value);
      if (typeof value === "object") {
        const raw = row.original[col.id as keyof typeof row.original] as any;
        return raw?.date ?? raw?.formatted_date ?? "";
      }
      return value;
    });
  });

  return { headers, data, columns, rowsToExport };
};

const getExportMeta = <T extends RowData>(
  table: MRT_TableInstance<T>,
  rows: MRT_Row<T>[],
  pageName: string
) => {
  const dateStamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
  const fileBaseName = `${pageName} Export - ${dateStamp}`;
  const exportData = buildExportData(table, rows, true);
  return { fileBaseName, ...exportData };
};


export const exportVisibleTableCSV = <T extends Record<string, any>>(
  table: MRT_TableInstance<T>,
  rows: MRT_Row<T>[],
  pageName: string
) => {
  if (!rows.length) return;

  const { fileBaseName, headers, data } = getExportMeta(table, rows, pageName);

  const csv = [
    headers.join(","),
    ...data.map((row) =>
      row
        .map((cell) =>
          `"${String(cell).replace(/"/g, '""')}"`
        )
        .join(",")
    ),
  ].join("\n");

  const blob = new Blob([csv], {
    type: "text/csv;charset=utf-8;",
  });

  triggerDownload(blob, `${fileBaseName}.csv`);
};

export const exportVisibleTableExcel = <T extends RowData>(
  table: MRT_TableInstance<T>,
  rows: MRT_Row<T>[],
  pageName: string
) => {
  if (!rows.length) return;

  const { fileBaseName, headers, data, columns, rowsToExport } = getExportMeta(
    table,
    rows,
    pageName
  );
  const worksheet = XLSX.utils.aoa_to_sheet([
    headers,
    ...data,
  ]);

  headers.forEach((_, c) => {
    const cell = worksheet[XLSX.utils.encode_cell({ r: 0, c })];
    if (cell) {
      cell.s = {
        font: { bold: true },
        alignment: { horizontal: "center", vertical: "center" },
      };
    }
  });

  worksheet["!cols"] = columns.map((c) => ({
    wch: Math.max(
      String(c.columnDef.header).length + 2,
      14
    ),
  }));

  worksheet["!freeze"] = { xSplit: 0, ySplit: 1 };

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(
    workbook,
    worksheet,
    fileBaseName
  );

  XLSX.writeFile(workbook, `${fileBaseName}.xlsx`, {
    bookType: "xlsx",
    compression: true,
    cellStyles: true,
  });

  console.log(`Exported ${rowsToExport.length} rows (including children) to Excel`);
};